// components/InvoicesPage.js
//
// One row per monthly statement, with the two invoices it settles (two-supply netting):
// HandOff's invoice for the orders the retailer received, the self-billed invoice for the
// orders it sent, and the balance that is paid. The statement is the only thing that is
// paid; the invoices are never paid on their own, which is what the page says first.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  Collapse,
  Button,
  FormControlLabel,
  Switch,
  Link,
} from '@mui/material';
import { PictureAsPdf, InfoOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { sub } from 'date-fns';
import { dashboardAPI } from '../services/dashboard-api';

import {
  SETTLEMENT_STATUS_COLORS,
  SETTLEMENT_STATUS_LABELS,
  SETTLEMENT_STATUS_FILTERS,
  statementStatusKey,
  netDirectionLabel,
} from '../utils/settlementStatus';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatPeriod = (start, end) => {
  if (!start || !end) return '-';
  const s = new Date(start);
  const e = new Date(end);
  const monthYear = (d) => d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return monthYear(s);
  }
  return `${monthYear(s)} – ${monthYear(e)}`;
};

const DIRECTION_COLORS = {
  retailer_to_handoff: 'warning',
  handoff_to_retailer: 'success',
  balanced: 'default',
};

const InvoicesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const [testMode, setTestMode] = useState(isDevelopment);

  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [startDate, setStartDate] = useState(() => sub(new Date(), { months: 18 }));
  const [endDate, setEndDate] = useState(() => new Date());

  const [uninvoicedSummary, setUninvoicedSummary] = useState(null);

  const [showInfo, setShowInfo] = useState(
    () => localStorage.getItem('handoff_invoice_info_dismissed_v2') !== 'true'
  );

  const fetchStatements = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (startDate) params.start_date = startDate.toISOString().split('T')[0];
      if (endDate) params.end_date = endDate.toISOString().split('T')[0];
      if (testMode) params.test_mode = 'true';

      const response = await dashboardAPI.getSettlements(params);
      if (response.success) {
        setStatements(response.data.items || []);
        setTotalCount(response.data.pagination?.total_items || 0);
      } else {
        setError(response.error || 'Failed to load statements');
        setStatements([]);
        setTotalCount(0);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setStatements([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, startDate, endDate, testMode]);

  const ordersLabel = (n) => `${n} order${n === 1 ? '' : 's'}`;

  const fetchUninvoicedSummary = useCallback(async () => {
    try {
      const response = await dashboardAPI.getUninvoicedSummary();
      if (response.success) setUninvoicedSummary(response.data);
    } catch (err) {
      console.error('Failed to load uninvoiced summary:', err);
    }
  }, []);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  useEffect(() => {
    fetchUninvoicedSummary();
  }, [fetchUninvoicedSummary]);

  const download = async (e, fn) => {
    e.stopPropagation();
    try {
      await fn();
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  };

  const handleDismissInfo = () => {
    setShowInfo(false);
    localStorage.setItem('handoff_invoice_info_dismissed_v2', 'true');
  };

  const TableSkeletonRow = () => (
    <TableRow>
      {[1, 2, 3, 4, 5, 6, 7].map((item) => (
        <TableCell key={item}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  );

  const DocumentCell = ({ number, total, documentStatus, onPdf, onOpen }) => {
    if (!number) {
      return (
        <Typography variant="body2" color="textSecondary">
          –
        </Typography>
      );
    }
    const cancelled = documentStatus === 'cancelled';
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box>
          {onOpen ? (
            <Link
              component="button"
              variant="body2"
              fontWeight="medium"
              underline="hover"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              sx={{ textDecoration: cancelled ? 'line-through' : undefined }}
            >
              {number}
            </Link>
          ) : (
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{ textDecoration: cancelled ? 'line-through' : undefined }}
            >
              {number}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" display="block">
            {formatCurrency(total)} incl. VAT
          </Typography>
        </Box>
        {!cancelled && (
          <Tooltip title="Download PDF">
            <IconButton size="small" onClick={(e) => download(e, onPdf)}>
              <PictureAsPdf fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Invoices
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* How invoicing works */}
        <Collapse in={showInfo}>
          <Alert
            severity="info"
            icon={<InfoOutlined />}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleDismissInfo}>
                Dismiss
              </Button>
            }
          >
            <Typography variant="subtitle2" gutterBottom>
              How invoicing works
            </Typography>
            <Typography variant="body2" gutterBottom>
              Each month you get one e-mail with three documents:
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              <li>
                <strong>HandOff&apos;s invoice to you.</strong> 10% of the clicked product for every order your
                shop received from a partner.
              </li>
              <li>
                <strong>Your invoice to HandOff.</strong> 8% of the clicked product for every order you sent to
                a partner. HandOff prepares this invoice in your name (self-billing). You do not make it yourself.
              </li>
              <li>
                <strong>A statement with the difference.</strong> Only the difference is paid: you pay HandOff, or
                HandOff pays you. The two invoices are never paid separately.
              </li>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The statement shows the amount, the due date and the account. Book both invoices like any other
              invoice. Not right? Reply to the e-mail within 14 days.
            </Typography>
          </Alert>
        </Collapse>

        {!showInfo && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={<InfoOutlined />}
              onClick={() => {
                setShowInfo(true);
                localStorage.removeItem('handoff_invoice_info_dismissed_v2');
              }}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              How invoicing works
            </Button>
          </Box>
        )}

        {uninvoicedSummary && (uninvoicedSummary.received.count > 0 || uninvoicedSummary.sent.count > 0) && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            On your next statement so far: <strong>{ordersLabel(uninvoicedSummary.received.count)}</strong> you
            received ({formatCurrency(uninvoicedSummary.received.amount)}) and{' '}
            <strong>{ordersLabel(uninvoicedSummary.sent.count)}</strong> you sent (
            {formatCurrency(uninvoicedSummary.sent.amount)}). Amounts excl. VAT.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Period from"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Period to"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    displayEmpty
                    notched
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    {SETTLEMENT_STATUS_FILTERS.map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                {totalCount} statement{totalCount !== 1 ? 's' : ''}
              </Typography>
              {isDevelopment && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={testMode}
                      onChange={(e) => {
                        setTestMode(e.target.checked);
                        setPage(0);
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" color="textSecondary">
                      Test mode
                    </Typography>
                  }
                />
              )}
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Statement</TableCell>
                    <TableCell>HandOff → you</TableCell>
                    <TableCell>You → HandOff</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <>
                      <TableSkeletonRow />
                      <TableSkeletonRow />
                      <TableSkeletonRow />
                    </>
                  ) : statements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box py={4}>
                          <Typography color="textSecondary" gutterBottom>
                            No statements found
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {statusFilter
                              ? 'Try changing the status filter.'
                              : 'Statements appear here after each monthly run, with both invoices.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    statements.map((st) => {
                      const statusKey = statementStatusKey(st);
                      return (
                        <TableRow key={st.id} hover>
                          <TableCell>
                            <Typography variant="body2">{formatPeriod(st.period_start, st.period_end)}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Issued {formatDate(st.sent_at || st.issued_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight="medium">
                                {st.statement_number}
                              </Typography>
                              <Tooltip title="Download statement PDF">
                                <IconButton
                                  size="small"
                                  onClick={(e) => download(e, () => dashboardAPI.downloadStatementPdf(st.id, { testMode }))}
                                >
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <DocumentCell
                              number={st.invoice_number}
                              total={st.invoice_total}
                              documentStatus={st.invoice_document_status}
                              onPdf={() => dashboardAPI.downloadInvoicePdf(st.invoice_id, { testMode })}
                              onOpen={() => navigate(`/invoices/${st.invoice_id}${testMode ? '?test_mode=true' : ''}`)}
                            />
                          </TableCell>
                          <TableCell>
                            <DocumentCell
                              number={st.self_bill_number}
                              total={st.self_bill_total}
                              documentStatus={st.self_bill_document_status}
                              onPdf={() => dashboardAPI.downloadSelfBillPdf(st.self_bill_id, { testMode })}
                              onOpen={() => navigate(`/self-bills/${st.self_bill_id}${testMode ? '?test_mode=true' : ''}`)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(st.cash_amount)}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={netDirectionLabel(st)}
                              color={DIRECTION_COLORS[st.net_direction] || 'default'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(st.due_date)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={SETTLEMENT_STATUS_LABELS[statusKey]}
                              color={SETTLEMENT_STATUS_COLORS[statusKey]}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default InvoicesPage;
